import React from 'react'
import { PRGlassProperties } from '@sketch-hq/sketch-web-renderer'
import { useHandleCopyValue } from '@sketch/modules-common'
import { Flex, Box, Tooltip } from '@sketch/components'
import { PopoverHeader } from '../../../../PopoverHeader'
import { AttributeList } from '../../../components'
import { FullCopyAttribute } from '../../../components/FullCopyAttribute'
import { getDirtyAttributes } from '../../../components/Style'
import { CopyButton, CopyIcon } from './Blur.styles'

interface GlassPopoverProps extends PRGlassProperties {
  copyValue: string
  onClose?: () => void
  originalValues?: PRGlassProperties
}

export const GlassPopover = ({
  copyValue,
  onClose,
  brightness,
  distortion,
  depth,
  chromaticAberration,
  saturation,
  originalValues,
}: GlassPopoverProps) => {
  const {
    handleCopyValue,
    copyTooltipText,
    handleEnterCopiableArea,
    handleLeaveCopiableArea,
    copyTooltipVisible,
  } = useHandleCopyValue(copyValue)

  const formatValue = (value: number, multiplier: number = 10) =>
    (value * multiplier).toFixed(0)

  return (
    <>
      <PopoverHeader
        onClose={onClose}
        title="Glass"
        extraAction={
          <Tooltip content={copyTooltipText} visible={copyTooltipVisible}>
            <Flex alignItems="center">
              <CopyButton
                onClick={handleCopyValue}
                onMouseEnter={handleEnterCopiableArea}
                onMouseLeave={handleLeaveCopiableArea}
              >
                <CopyIcon />
              </CopyButton>
            </Flex>
          </Tooltip>
        }
      />
      {/* @TODO: to be added when we have a way to preview blurs, WEB-1777 */}
      {/* <SwatchContainer /> */}
      <Box pl={5} width="300px" mt={3} pb={4}>
        <AttributeList>
          <FullCopyAttribute
            label="Brightness"
            value={`${formatValue(brightness || 0, 100)}%`}
            dirtyAttributes={getDirtyAttributes({
              originalValueKey: 'brightness',
              valueToDisplay: `${formatValue(originalValues?.brightness || 0, 100)}%`,
              originalValues,
              labelToDisplay: 'Brightness',
            })}
          />
          <FullCopyAttribute
            label="Distortion"
            value={distortion.toFixed(1)}
            dirtyAttributes={getDirtyAttributes({
              originalValueKey: 'distortion',
              valueToDisplay: (
                originalValues?.distortion.toFixed(1) || 0
              ).toString(),
              originalValues,
              labelToDisplay: 'Distortion',
            })}
          />
          <FullCopyAttribute
            label="Depth"
            value={formatValue(depth)}
            dirtyAttributes={getDirtyAttributes({
              originalValueKey: 'depth',
              valueToDisplay: formatValue(originalValues?.depth || 0),
              originalValues,
              labelToDisplay: 'Depth',
            })}
          />
          <FullCopyAttribute
            label="Chromatic Aberration"
            value={formatValue(chromaticAberration, 1)}
            dirtyAttributes={getDirtyAttributes({
              originalValueKey: 'chromaticAberration',
              valueToDisplay: formatValue(
                originalValues?.chromaticAberration || 0,
                1
              ),
              originalValues,
              labelToDisplay: 'Chromatic Aberration',
            })}
          />
          <FullCopyAttribute
            label="Saturation"
            value={`${formatValue(saturation || 0, 100)}%`}
            dirtyAttributes={getDirtyAttributes({
              originalValueKey: 'saturation',
              valueToDisplay: `${formatValue(originalValues?.saturation || 0, 100)}%`,
              originalValues,
              labelToDisplay: 'Saturation',
            })}
          />
        </AttributeList>
      </Box>
    </>
  )
}
